import { Component, OnInit, OnDestroy, NgZone, ViewChild } from "@angular/core";

import { Subscription } from "rxjs";

import { ModalDirective } from "ngx-bootstrap/modal";

import {
  DataArray,
  AuthorizationService,
  SectionService,
  UserService,
  LanguageService,
  CompanyService,
  MinesiteService,
  Section,
  Language,
  Company,
  Minesite,
  User,
  Permission,
  SectionPermission,
  GlobalService,
  AssociationService,
  UserCompany,
} from "./../../../shared/index";
import { NgForm } from "@angular/forms";

@Component({
  selector: "user-list",
  templateUrl: "./user-list.html",
  standalone: false,
})
export class UserListComponent implements OnInit, OnDestroy {
  @ViewChild("userEditModal") editUserModal: ModalDirective;
  @ViewChild("usernameEditModal") editUsernameModal: ModalDirective;
  @ViewChild("userform") userform: NgForm;

  public users: DataArray<User> = new DataArray<User>(1);
  public user: User = new User();
  public newUsername = "";
  public permissions: Permission[] = [];
  public languages: Language[] = [];
  public sections: Section[] = [];
  public companies: Company[] = [];
  public minesites: Minesite[] = [];
  public sectionPermissions: DataArray<SectionPermission> =
    new DataArray<SectionPermission>(10);

  public selected: number[] = [];
  public associatedSelected: number[] = [];
  private subscriptions: Subscription[] = [];

  public sortField = "FirstName";
  public ascending = true;
  public currentPage = 1;
  public total = 0;
  public currentUser = "";
  public assignCompanies = false;
  public companyError = false;
  public minesiteError = false;

  public processing = true;

  //NOTE: This property was referenced in the template as `[class.disabled]="disable"`
  //	However, it was not defined in the component, and switching to strictTemplate: true in tsconfig.json caused an error.
  //	Adding the property here to resolve the error since it resolved to falsy before
  //  Assumption: this may have been intended to be part of the user object, but was not implemented.
  public disable = false;

  constructor(
    private _ngZone: NgZone,
    private _userService: UserService,
    private _authorizationService: AuthorizationService,
    private _sectionService: SectionService,
    private _languageService: LanguageService,
    private _companyService: CompanyService,
    private _minesiteService: MinesiteService,
    private _globalService: GlobalService,
    private _associationService: AssociationService
  ) {
    this.subscriptions.push(
      this._userService.userCreated.subscribe((info: User) => {
        if (info.PermissionId == 1) {
          this._userService.saveSectionPermissions(
            info.Id,
            this.sectionPermissions.Data
          );
          for (const sectionPermission of this.sectionPermissions.Data) {
            sectionPermission.Allowed = true;
          }
        }
        if (info.UserName == this.user.UserName && this.editUserModal.isShown) {
          this.user.Id = info.Id;
          this.assignCompanies = true;
        }
        this._userService.getUsers(this.currentPage);
      })
    );

    this.subscriptions.push(
      this._userService.usersChange.subscribe((users: User[]) => {
        this._ngZone.run(() => {
          this.total = this._userService.totalUsers;

          this.users.init();
          this.currentPage = this._userService.currentPage;

          for (const user of users) {
            this.users.update(user, true);
          }

          this.processing = false;
        });
      })
    );

    this.subscriptions.push(
      this._userService.userChange.subscribe((user: User) => {
        this._ngZone.run(() => {
          this.total = this._userService.totalUsers;
          this.user = user;
          this.users.update(user);
        });
      })
    );

    this.subscriptions.push(
      this._userService.permissionChange.subscribe((info: any) => {
        this._ngZone.run(() => {
          this.permissions = info;
        });
      })
    );

    this.subscriptions.push(
      this._userService.languageChange.subscribe((info: any) => {
        this._ngZone.run(() => {
          this.languages = info;
        });
      })
    );

    this.subscriptions.push(
      this._sectionService.sectionsChange.subscribe((info: any) => {
        this._ngZone.run(() => {
          this.sections = info;
          this.sectionPermissions.init();
          for (const section of this.sections) {
            const sectionPermission: SectionPermission =
              new SectionPermission();

            sectionPermission.Allowed = true;
            sectionPermission.SectionId = section.Id;
            sectionPermission.Name = section.Name;

            this.sectionPermissions.push(sectionPermission);
          }
        });
      })
    );

    this.subscriptions.push(
      this._companyService.companysChange.subscribe((info: any) => {
        this._ngZone.run(() => {
          if (info.companies.length > 0) this.companies = info.companies;
          //TODO: add handling: also receives info.isFinalPage
        });
      })
    );

    this.subscriptions.push(
      this._companyService.allCompaniesChange.subscribe((info: any) => {
        this._ngZone.run(() => {
          if (info.length > 0) this.companies = info;
        });
      })
    );

    this.subscriptions.push(
      this._minesiteService.companyMinesiteChanges.subscribe((info: any) => {
        this._ngZone.run(() => {
          //This will only run after all other operations complete, cannot rely on data changing during operations
          this.user.Company.Minesites = new DataArray<Minesite>(1);
          this.user.Company.Minesites.init();
          for (const minesite of info.Minesites) {
            const newMinesite = $.extend(new Minesite(), minesite);
            this.user.Company.Minesites.push(newMinesite);
            if (this.user.MinesiteId == newMinesite.Id) {
              this.user.Minesite == newMinesite;
            }
          }
        });
      })
    );

    this.subscriptions.push(
      this._globalService.deleteResult.subscribe((result: boolean) => {
        if (result) {
          this._userService.deleteUser(this.user.Id, this.currentUser);
        }
      })
    );

    this.subscriptions.push(
      this._associationService.userCompaniesChanged.subscribe(
        (result: UserCompany[]) => {
          this._ngZone.run(() => {
            this.user.Companies = result;
            this.associatedSelected = [];
            this.selected = [];
          });
        }
      )
    );
  }

  ngOnInit(): void {
    this._userService.ascending = true;
    this._userService.orderBy = "FirstName";
    this._userService.getUsers(this.currentPage);

    this.users.init();
    this.getPage(1);
    this._sectionService.getSections();
    this._userService.getLanguages();
    this._userService.getPermissions();
    this._companyService.getAllCompanies();

    this.currentUser = this._authorizationService.userName;
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  public getPage(page: number): void {
    this._userService.getUsers(page);
  }

  public setUserCompany(event?: any): void {
    //console.warn('setUserCompany(', event, ': ', typeof event, ')');

    if (this.user.PermissionId < 3 || this.user.PermissionId == 5) {
      this.user.Company = null;
      this.user.CompanyId = null;
      this.user.Minesite = null;
      this.user.MinesiteId = null;
      return;
    }
    if (this.user.CompanyId > 0) {
      this.companyError = false;
      this.user.Company = this.companies.find(
        (c) => c.Id === this.user.CompanyId
      );

      this.setUserMinesite(1);
      if (this.user.Company && this.user.PermissionId == 4) {
        this._minesiteService.getMinesitesForCompany(this.user.CompanyId);
      }
    } else {
      this.companyError = event == undefined;
    }
  }

  public setUserMinesite(event?: any) {
    //console.warn('setUserMinesite(', event, ': ', typeof event, ')');

    if (this.user.PermissionId != 4) {
      this.user.Minesite = null;
      this.user.MinesiteId = null;
      return;
    }
    this.minesiteError = event == "null";
    this.user.Minesite = this.user.Company.Minesites.GetByMap(
      0,
      this.user.MinesiteId
    );
  }

  public sortBy(field: string): void {
    if (this._userService.orderBy == field) {
      this._userService.ascending = !this._userService.ascending;
    } else {
      this._userService.ascending = true;
      this._userService.orderBy = field;
      this.sortField = field;
    }
    this.ascending = this._userService.ascending;
    this._userService.getUsers(this.currentPage);
  }

  public edit(user: User): void {
    this.user = user;
    ///this._userService.getUser(user.Id);
    //console.warn('edit: user: ', JSON.stringify(this.user));
    this.setUserCompany();

    if (this.user.PermissionId == 1) {
      for (const section of this.user.SectionPermissions) {
        this.sectionPermissions.GetByMap(0, section["SectionId"]).Allowed =
          section["Allowed"];
      }
    }
    this.associatedSelected = [];
    this.selected = [];
    this.editUserModal.show();
  }

  public isSort(field: string) {
    return field == this.sortField;
  }

  public isSortDesc(field: string) {
    return this.isSort(field) && !this.ascending;
  }

  public remove(user: User): void {
    this.user = user;
    this._globalService.confirmDelete("user", user.UserName, user.Id);
  }

  public resetPassword() {
    this._userService.resetPassword(this.user.Id);
  }

  public resetPasswordandQuestions() {
    this._userService.resetPasswordandQuestions(this.user.Id);
  }

  public toggle(user: User): void {
    user.Active = !user.Active;
    this._userService.toggleUser(user.Id, user.Active, this.currentUser);
  }

  public validateFormDetails(): void {
    if (this.user.PermissionId >= 3 && this.user.PermissionId <= 4) {
      this.companyError = this.user.CompanyId > 0 ? false : true;
    }
    if (this.user.PermissionId == 4) {
      this.minesiteError = this.user.MinesiteId > 0 ? false : true;
    }
    if (this.user.PermissionId < 3 || this.user.PermissionId == 5) {
      this.complete();
    } else {
      if (!this.companyError && !this.minesiteError) {
        this.complete();
      }
    }
  }

  public complete(): void {
    if (this.user.Id === 0) {
      this.user.CreatedBy = this.currentUser;
      this._userService.createUser(this.user);
    } else {
      this._userService.save(this.user);
    }
    this.editUserModal.hide();
    this.assignCompanies = false;

    //2023-11-30: Bugfix, after changing minesite, reloading window results in unknonw bug that prevents minesite from being selected in dropdown.
    //	Was unable to identify exact reason why. Resetting user each time it is closed works as solution.
    this.user = new User();
  }

  private isEmail(stringToCheck: string): boolean {
    const pattern = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    if (!pattern.test(stringToCheck)) {
      return false;
    } else {
      return true;
    }
  }
  public updateUsername(): void {
    if (this.isEmail(this.newUsername)) {
      this._userService.changeUsername(this.user.UserName, this.newUsername);
      this.newUsername = "";
      this.editUsernameModal.hide();
      this._associationService.getUserCompaniesList(this.user.Id);
    } else {
      alert("Enter a valid new username (Email)");
    }
  }

  public showChangeUserNameModal(): void {
    this.editUsernameModal.show();
  }

  public cancelUserNameModal(): void {
    this.newUsername = "";
    this.editUsernameModal.hide();
  }

  public saveAndAssign(): void {
    if (this.user.Id === 0) {
      this.user.CreatedBy = this.currentUser;
      this._userService.createUser(this.user);
    }
  }

  public cancel(): void {
    this.editUserModal.hide();

    //2023-11-30: Bugfix, after changing minesite, reloading window results in unknonw bug that prevents minesite from being selected in dropdown.
    //	Was unable to identify exact reason why. Resetting user each time it is closed works as solution.
    this.user = new User();

    this.assignCompanies = false;
    this.companyError = false;
    this.minesiteError = false;
    this._userService.getUsers(this.currentPage);
  }

  public PrintCBChanged(): void {
    this.user.AllowPrinting = !this.user.AllowPrinting;
  }

  public addUser(): void {
    this.assignCompanies = false;
    this.user = new User();
    for (const section of this.sectionPermissions.Data) {
      section.Allowed = true;
    }
    this.userform.resetForm();
    this.user.LanguageName = null;
    this.user.AllowPrinting = true;
    this.user.ResetPassword = true;
    this.user.ResetSecurityQuestions = true;
    this.user.AssignedCompanies = new Array<Company>();
    this.editUserModal.show();
  }

  public selectAllCompanies(): void {
    for (const company of this.companies) {
      this.selected.push(company.Id);
    }
  }

  public selectAllAssociated(): void {
    for (const company of this.companies) {
      this.associatedSelected.push(company.Id);
    }
  }

  public selectFromAll(companyId: number): void {
    if (this.selected.find((value) => value == companyId)) {
      const temp: number[] = [];
      for (const tempCompanyId of this.selected) {
        if (tempCompanyId != companyId) {
          temp.push(tempCompanyId);
        }
      }
      this.selected = temp;
    } else {
      this.selected.push(companyId);
    }
  }

  public selectAssociated(companyId: number): void {
    if (this.associatedSelected.find((value) => value == companyId)) {
      const temp: number[] = [];
      for (const docId of this.associatedSelected) {
        if (docId != companyId) {
          temp.push(docId);
        }
      }
      this.associatedSelected = temp;
    } else {
      this.associatedSelected.push(companyId);
    }
  }

  public removeSelected(): void {
    this._associationService.removeUserCompanyAssociation(
      this.user.Id,
      this.associatedSelected
    );
  }

  public associateSelected(): void {
    this._associationService.associateUserToCompanies(
      this.user.Id,
      this.selected
    );
  }

  public companyList(assigned: boolean): Company[] {
    if (assigned) {
      return this.companies.filter((c) => {
        return this.isCompanyAssigned(c.Id);
      });
    } else {
      return this.companies.filter((c) => {
        return !this.isCompanyAssigned(c.Id);
      });
    }
  }

  public isCompanySelected(companyId: number): boolean {
    return (
      this.selected.filter((value, index) => {
        return value == companyId;
      }).length > 0
    );
  }

  public isCompanyAssigned(companyId: number): boolean {
    if (this.user.Companies.length === 0) {
      return true;
    } else {
      if (this.user.Companies.find((value) => value.CompanyId === companyId)) {
        return true;
      }
    }
    return false;
  }

  public isAssociatedSelected(companyId: number): boolean {
    return (
      this.associatedSelected.filter((value, index) => {
        return value == companyId;
      }).length > 0
    );
  }
}
